import React, { useEffect, useRef, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/api";
import { toast } from "sonner";
import "./Landing.css";

// Emergent-managed Google auth
function googleLogin() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirect = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
}


const STATES = [
  { cls: "st-blank", pill: "blank", pillCls: "on-blank", hint: "First time. Enter the weight and reps you'd normally do, then tap a set.", hintCls: "", showTarget: false, target: null, ticks: [0, 0, 0, 0], enc: 0, pulseFrom: -1 },
  { cls: "st-amber", pill: "amber", pillCls: "on-amber", hint: "Predicted from last time · 70 kg × 8 avg", hintCls: "amber-txt", showTarget: true, target: { w: "70 kg", reps: "× 8" }, ticks: [1, 1, 0, 0], enc: 1, pulseFrom: 2 },
  { cls: "st-valid", pill: "validated", pillCls: "on-valid", hint: "All sets hit target. +2.5 kg next session.", hintCls: "", showTarget: true, target: { w: "72.5 kg", reps: "× 8" }, ticks: [2, 2, 2, 2], enc: 2, pulseFrom: -1 },
];

function DemoCard() {
  const [idx, setIdx] = useState(0);
  const timerRef = useRef(null);
  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return;
    timerRef.current = setInterval(() => setIdx((i) => (i + 1) % STATES.length), 3400);
    return () => clearInterval(timerRef.current);
  }, []);
  const s = STATES[idx];
  return (
    <div className={`lp-dcard ${s.cls}`} data-testid="lp-demo-card">
      <div className="lp-dname">Barbell squat</div>
      <div className={`lp-dhint ${s.hintCls}`}>{s.hint}</div>
      <div className="lp-dtarget" style={{ visibility: s.showTarget ? "visible" : "hidden" }}>
        {s.target ? (<>{s.target.w} <span>{s.target.reps}</span></>) : "—"}
      </div>
      <div className="lp-dticks">
        {[0, 1, 2, 3].map((t) => {
          const v = s.ticks[t];
          return (
            <div key={t} className={`lp-dtick ${v === 2 ? "on" : ""} ${s.pulseFrom === t ? "pulsing" : ""}`}>
              {v === 2 || v === 1 ? "8" : t + 1}
            </div>
          );
        })}
      </div>
      <div className="lp-dstate-label">
        <span className={`lp-dstate-pill ${s.pillCls}`}>{s.pill}</span>
        <span className="lp-mono" style={{ fontSize: 11, color: "var(--lp-steel-dim)" }}>enc {s.enc}/2</span>
      </div>
    </div>
  );
}

function MacroDonutBig() {
  return (
    <svg width="180" height="180" viewBox="0 0 180 180" data-testid="lp-donut">
      <circle cx="90" cy="90" r="72" fill="none" stroke="#1F1F1F" strokeWidth="17" />
      <circle cx="90" cy="90" r="72" fill="none" stroke="#34D399" strokeWidth="17" strokeDasharray="452" strokeDashoffset="452" strokeLinecap="round">
        <animate attributeName="stroke-dashoffset" from="452" to="316" dur="1.2s" fill="freeze" begin="0.2s" />
      </circle>
      <circle cx="90" cy="90" r="72" fill="none" stroke="#FB7A4B" strokeWidth="17" strokeDasharray="452" strokeDashoffset="452" strokeLinecap="round" transform="rotate(101 90 90)">
        <animate attributeName="stroke-dashoffset" from="452" to="196" dur="1.2s" fill="freeze" begin="0.35s" />
      </circle>
      <circle cx="90" cy="90" r="72" fill="none" stroke="#F5C84B" strokeWidth="17" strokeDasharray="452" strokeDashoffset="452" strokeLinecap="round" transform="rotate(211 90 90)">
        <animate attributeName="stroke-dashoffset" from="452" to="390" dur="1.2s" fill="freeze" begin="0.5s" />
      </circle>
      <text x="90" y="86" textAnchor="middle" fontFamily="JetBrains Mono" fontSize="30" fontWeight="700" fill="#F2F1EC">2565</text>
      <text x="90" y="106" textAnchor="middle" fontFamily="Inter" fontSize="12" fill="#83878E">kcal / day</text>
    </svg>
  );
}

function Check() {
  return (
    <svg viewBox="0 0 12 12" fill="none" style={{ width: 11, height: 11 }}>
      <path d="M2 6l3 3 5-6" stroke="#B6F500" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function Landing() {
  const { user, loading, register } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  if (loading) {
    return <div style={{ minHeight: "100vh", background: "#050505", color: "#54585F", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'JetBrains Mono',monospace", fontSize: 11, letterSpacing: "0.2em" }}>CHARGE1</div>;
  }
  if (user) {
    const needsOnboarding = user.goal == null || user.daysPerWeek == null || user.age == null;
    return <Navigate to={needsOnboarding ? "/onboarding" : "/dashboard"} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      if (password.length < 8) throw new Error("Password must be at least 8 characters.");
      await register({ email, password, unitsPref: "metric" });
      toast.success("Account created.");
      nav("/onboarding");
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="lp-root">
      <nav className="lp-nav">
        <div className="lp-wrap">
          <div className="lp-brand" data-testid="lp-brand">charge<span>1</span></div>
          <Link to="/login" className="lp-navlink" data-testid="lp-nav-signin">Already have an account? Sign in</Link>
        </div>
      </nav>

      <section className="lp-hero">
        <div className="lp-wrap lp-hero-grid">
          <div>
            <div className="lp-eyebrow"><i />18+ · systematic training</div>
            <h1 data-testid="lp-h1">Stop guessing<br />your <em>numbers.</em></h1>
            <p className="lp-hero-sub">Charge1 tells you exactly what to lift, learns from what you actually do, and gets your macros right. Nothing else.</p>

            <div className="lp-proofs">
              <div className="lp-proof"><span className="x"><Check /></span>No calibration test — you lift, it learns.</div>
              <div className="lp-proof"><span className="x"><Check /></span>No AI coach, no fluff — just the number and the rep.</div>
              <div className="lp-proof"><span className="x"><Check /></span>Macros calculated the moment you set your stats.</div>
            </div>

            <div className="lp-form-card" id="signup">
              <div className="lp-oauth">
                <button className="lp-btn lp-btn-google" onClick={googleLogin} data-testid="lp-google">
                  <svg width="16" height="16" viewBox="0 0 18 18"><path fill="#4285F4" d="M17.6 9.2c0-.6-.1-1.2-.2-1.8H9v3.4h4.8c-.2 1.1-.8 2.1-1.8 2.7v2.3h2.9c1.7-1.6 2.7-3.9 2.7-6.6z" /><path fill="#34A853" d="M9 18c2.4 0 4.5-.8 6-2.2l-2.9-2.3c-.8.5-1.9.9-3.1.9-2.4 0-4.4-1.6-5.1-3.8H.9v2.3C2.4 15.9 5.5 18 9 18z" /><path fill="#FBBC05" d="M3.9 10.6c-.2-.5-.3-1.1-.3-1.6s.1-1.1.3-1.6V5.1H.9C.3 6.3 0 7.6 0 9s.3 2.7.9 3.9l3-2.3z" /><path fill="#EA4335" d="M9 3.6c1.3 0 2.5.5 3.4 1.3l2.6-2.6C13.5.8 11.4 0 9 0 5.5 0 2.4 2.1.9 5.1l3 2.3C4.6 5.2 6.6 3.6 9 3.6z" /></svg>
                  Continue with Google
                </button>
              </div>
              <div className="lp-divider">or with email</div>
              <form onSubmit={submit} noValidate>
                <div className="lp-field">
                  <label>Email</label>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" required data-testid="lp-email" />
                </div>
                <div className="lp-field">
                  <label>Password</label>
                  <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="8+ characters" minLength={8} required data-testid="lp-password" />
                </div>
                {err && <div className="lp-err" data-testid="lp-error">{err}</div>}
                <button className="lp-btn lp-btn-primary" disabled={busy} data-testid="lp-submit">{busy ? "…" : "Create account"}</button>
              </form>
              <p className="lp-legal">By continuing, you confirm you're <b>18 or older</b> and agree to the Terms and Privacy Policy.</p>
              <p className="lp-signin">Already training with us? <Link to="/login" data-testid="lp-signin-link">Sign in</Link></p>
            </div>
          </div>

          <div className="lp-demo-wrap">
            <div className="lp-phone">
              <div className="lp-notch" />
              <div className="lp-demo-inner">
                <div className="lp-demo-label">Barbell squat · live example</div>
                <DemoCard />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="lp-section">
        <div className="lp-wrap">
          <div className="lp-section-head">
            <div className="lp-section-eyebrow">How it actually works</div>
            <h2>Three states. No test, no guessing, no AI making it up.</h2>
            <p className="lp-section-sub">Most apps ask you to max out on day one, or hand you a number an algorithm invented. Charge1 asks what you actually lifted, twice, then takes over.</p>
          </div>
          <div className="lp-steps">
            <div className="lp-step" data-testid="lp-step-blank">
              <div className="lp-step-swatch lp-sw-blank" />
              <h3>Blank</h3>
              <p>First time on a lift, the box is empty. You enter the weight and reps you'd normally do. Nothing invented, nothing guessed.</p>
            </div>
            <div className="lp-step" data-testid="lp-step-amber">
              <div className="lp-step-swatch lp-sw-amber" />
              <h3>Predicted</h3>
              <p>Second time, it shows last session's numbers as a prediction, clearly labeled as one — not a fact, just your own recent history.</p>
            </div>
            <div className="lp-step" data-testid="lp-step-valid">
              <div className="lp-step-swatch lp-sw-valid" />
              <h3>Validated</h3>
              <p>From here, the progression engine takes over. Clean reps, it climbs. Miss the target, it eases. Your data, not a formula's guess.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="lp-macro-band">
        <div className="lp-wrap lp-macro-inner">
          <div className="lp-donut-wrap"><MacroDonutBig /></div>
          <div className="lp-macro-copy">
            <div className="lp-section-eyebrow">Your macros, day one</div>
            <h2>Set your stats. Get your split, not a spreadsheet.</h2>
            <p className="lp-section-sub">Calories, protein, carbs and fat, computed from your own weight and body fat the moment you finish setup. A suggestion to hit, not a food log to maintain.</p>
            <div className="lp-macro-list">
              <div className="lp-macro-item"><span className="lp-dot" style={{ background: "#34D399" }} />Protein</div>
              <div className="lp-macro-item"><span className="lp-dot" style={{ background: "#FB7A4B" }} />Carbs</div>
              <div className="lp-macro-item"><span className="lp-dot" style={{ background: "#F5C84B" }} />Fat</div>
            </div>
          </div>
        </div>
      </section>

      <footer className="lp-foot">
        <div className="lp-wrap lp-foot-row">
          <div className="lp-brand" style={{ fontSize: 17 }}>charge<span>1</span></div>
          <p className="lp-foot-note">Charge1 is a training and nutrition-suggestion tool for lifters 18 and older. It does not replace medical or professional advice.</p>
        </div>
      </footer>
    </div>
  );
}
